type AnalyticsPayload = Record<string, unknown> | undefined;

export interface AnalyticsEvent {
  name: string;
  payload?: AnalyticsPayload;
  timestamp: string;
}

type AnalyticsListener = (event: AnalyticsEvent) => void;

const ANALYTICS_STORAGE_KEY = 'industrial-audit.analytics.queue';
const MAX_QUEUE_LENGTH = 100;

const listeners = new Set<AnalyticsListener>();

const enqueueEvent = (event: AnalyticsEvent) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const raw = window.localStorage.getItem(ANALYTICS_STORAGE_KEY);
    const queue: AnalyticsEvent[] = raw ? JSON.parse(raw) : [];
    queue.push(event);
    const trimmed = queue.slice(-MAX_QUEUE_LENGTH);
    window.localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[analytics] Failed to persist event', error);
    }
  }
};

export function trackEvent(name: string, payload?: AnalyticsPayload): void {
  const event: AnalyticsEvent = {
    name,
    payload,
    timestamp: new Date().toISOString(),
  };

  enqueueEvent(event);

  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[analytics] Listener error', error);
      }
    }
  });

  if (import.meta.env.DEV) {
    console.info('[analytics]', name, payload ?? {});
  }
}

export function subscribeAnalytics(listener: AnalyticsListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function readQueuedEvents(): AnalyticsEvent[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(ANALYTICS_STORAGE_KEY);
    if (!raw) return [];
    const queue = JSON.parse(raw) as AnalyticsEvent[];
    return Array.isArray(queue) ? queue : [];
  } catch {
    return [];
  }
}
